import { Injectable, inject } from '@angular/core';
import moment, { Moment } from 'moment';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
    compact as _compact,
    toString as _toString,
    uniq as _uniq,
} from 'lodash';
import { UnifiedQueryV2Service } from '../unified-query-v2.service';

// Whether lnwshop — the shop's stock system — still counts a marketplace
// order as sold, read from its nightly crawl (MongoDB lnw_crawler):
//
//   orders       one doc per lnwshop order; channelOrderSn is the platform
//                order number, _id the lnwshop order id
//   sales        one row per order line (orderId = lnwshop order id, as a
//                string; pid = the product), re-scraped EVERY night from
//                each product's full transaction log. type is 'sell' while
//                lnwshop still counts the line as sold, 'reversed' once it
//                has dropped out of the log.
//   crawl_runs   one doc per crawler run (crawler 'sales', status 'ok')
//
// lnwshop undoes a sale by deleting its sell rows (the units go back into
// stock). Since 2026-10-22 the sales crawler marks such a row type
// 'reversed' instead of leaving it behind as a stale 'sell' (it only does so
// for a product whose log it read, so a failed page cannot mark anything),
// and that mark is what this service reads first. Verified 2026-09-23
// against every return and failed delivery since June (e.g. Lazada
// 1126508118146468's sell row was last scraped the night before lnwshop
// reversed it).
//
// Rows the crawler has not marked yet are still judged the old way: a
// 'sell' row the latest crawl did not re-scrape — for a product that crawl
// DID read — is one lnwshop has undone. "For a product that crawl did read"
// matters: a run ends 'ok' with up to 30% of product pages failed, and a
// product that left the catalogue is not read at all. Its rows go stale
// without lnwshop undoing anything, so a stale row counts only if some
// other sell row of the same product was re-scraped by the latest crawl.
//
// Only these fields are read: the collections also hold buyer data.

export type LnwshopSaleState =
    // The sale still stands: lnwshop still counts the units as sold.
    // `lastSeenAt` is when its sell rows were last scraped.
    | { kind: 'standing'; lnwOrderId: string; lastSeenAt: Moment }
    // lnwshop undid the sale: the crawler marked its rows 'reversed', or
    // they stopped being scraped after `lastSeenAt` although the crawl
    // since read the product.
    | { kind: 'reversed'; lnwOrderId: string; lastSeenAt: Moment }
    // Cannot tell: no lnwshop order for this number, an order with no sell
    // rows, rows whose product the latest crawl did not read, or the lookup
    // failed.
    | {
          kind: 'unknown';
          reason: 'no_order' | 'no_sell_rows' | 'not_crawled' | 'unavailable';
      };

export interface LnwshopSaleLookup {
    // By marketplace order number.
    states: Record<string, LnwshopSaleState>;
    // Start of the latest successful sales crawl — what "standing" is as of.
    crawledAt: Moment | null;
    // The lookup failed outright: every state is 'unavailable'.
    failed: boolean;
}

interface SellRow {
    orderId: unknown;
    pid: unknown;
    type: unknown; // 'sell' | 'reversed'
    scrapedAt: unknown;
}

const isReversed = (row: SellRow): boolean =>
    _toString(row.type) === 'reversed';

const DB = 'lnw_crawler';
// Keys per query, to keep each request small.
const CHUNK = 100;

const chunk = <T>(items: T[]): T[][] =>
    Array.from({ length: Math.ceil(items.length / CHUNK) }, (_, i) =>
        items.slice(i * CHUNK, (i + 1) * CHUNK),
    );

const toMoment = (value: unknown): Moment | null => {
    const parsed = moment(_toString(value ?? ''), moment.ISO_8601, true);
    return parsed.isValid() ? parsed : null;
};

// The sell rows of each order number, and whether any is fresh.
const rowsByOrder = (
    orderNos: string[],
    orders: { _id: unknown; channelOrderSn: unknown }[],
    sells: SellRow[],
): Record<string, SellRow[] | null> => {
    const out: Record<string, SellRow[] | null> = {};
    for (const orderNo of orderNos) {
        const lnwIds = _uniq(
            orders
                .filter((order) => _toString(order.channelOrderSn) === orderNo)
                .map((order) => _toString(order._id)),
        );
        out[orderNo] = lnwIds.length
            ? sells.filter((sell) => lnwIds.includes(_toString(sell.orderId)))
            : null;
    }
    return out;
};

const lastScraped = (rows: SellRow[]): Moment | null => {
    const seen = rows.map((row) => toMoment(row.scrapedAt));
    // One unreadable time and the rows cannot be judged.
    if (seen.some((time) => time === null)) {
        return null;
    }
    return moment.max(seen as Moment[]);
};

// The products (pids) of orders whose unmarked sell rows all look stale —
// the ones whose coverage decides between 'reversed' and 'not_crawled'.
// An order the crawler already marked needs no coverage check.
export const stalePids = (
    orderNos: string[],
    orders: { _id: unknown; channelOrderSn: unknown }[],
    sells: SellRow[],
    crawledAt: Moment | null,
): string[] => {
    if (crawledAt === null) {
        return [];
    }
    const byOrder = rowsByOrder(orderNos, orders, sells);
    return _uniq(
        Object.values(byOrder).flatMap((rows) => {
            if (!rows?.length || rows.every(isReversed)) {
                return [];
            }
            const last = lastScraped(rows);
            return last && last.isBefore(crawledAt)
                ? rows.map((row) => _toString(row.pid))
                : [];
        }),
    );
};

// The lnwshop sale state of each order number, given what the crawl read.
// `coveredPids`: the products the latest crawl re-scraped. Pure, for the
// spec.
export const saleStates = (
    orderNos: string[],
    orders: { _id: unknown; channelOrderSn: unknown }[],
    sells: SellRow[],
    crawledAt: Moment | null,
    coveredPids: Set<string>,
): Record<string, LnwshopSaleState> => {
    const states: Record<string, LnwshopSaleState> = {};
    const byOrder = rowsByOrder(orderNos, orders, sells);
    for (const orderNo of orderNos) {
        const rows = byOrder[orderNo];
        if (rows === null) {
            states[orderNo] = { kind: 'unknown', reason: 'no_order' };
            continue;
        }
        if (rows.length === 0) {
            states[orderNo] = { kind: 'unknown', reason: 'no_sell_rows' };
            continue;
        }
        const lastSeenAt = lastScraped(rows);
        if (lastSeenAt === null) {
            states[orderNo] = { kind: 'unknown', reason: 'unavailable' };
            continue;
        }
        const lnwOrderId = _toString(rows[0].orderId);
        // The crawler saw every line drop out of lnwshop's log: undone, no
        // matter what the latest crawl covered.
        if (rows.every(isReversed)) {
            states[orderNo] = { kind: 'reversed', lnwOrderId, lastSeenAt };
            continue;
        }
        if (crawledAt === null) {
            states[orderNo] = { kind: 'unknown', reason: 'unavailable' };
            continue;
        }
        // lnwshop undoes a whole order, so one sell row still being scraped
        // means the sale stands.
        if (!lastSeenAt.isBefore(crawledAt)) {
            states[orderNo] = { kind: 'standing', lnwOrderId, lastSeenAt };
            continue;
        }
        states[orderNo] = rows.some((row) =>
            coveredPids.has(_toString(row.pid)),
        )
            ? { kind: 'reversed', lnwOrderId, lastSeenAt }
            : { kind: 'unknown', reason: 'not_crawled' };
    }
    return states;
};

const unavailable = (orderNos: string[]): LnwshopSaleLookup => {
    const states: Record<string, LnwshopSaleState> = {};
    for (const orderNo of orderNos) {
        states[orderNo] = { kind: 'unknown', reason: 'unavailable' };
    }
    return { states, crawledAt: null, failed: true };
};

@Injectable({
    providedIn: 'root',
})
export class LnwshopSaleService {
    private unifiedQuery = inject(UnifiedQueryV2Service);

    // Never errors: a failed lookup answers 'unavailable' for every order
    // (and says so), and the caller falls back to the platform rules.
    lookup(orderNumbers: string[]): Observable<LnwshopSaleLookup> {
        const orderNos = _uniq(_compact(orderNumbers.map((n) => _toString(n))));
        if (orderNos.length === 0) {
            return of({ states: {}, crawledAt: null, failed: false });
        }
        return forkJoin([
            this.latestSalesCrawl(),
            this.inChunks(orderNos, (part) =>
                this.unifiedQuery.query<any>({
                    db: DB,
                    col: 'orders',
                    query: { channelOrderSn: { $in: part } },
                    options: { projection: { _id: 1, channelOrderSn: 1 } },
                }),
            ),
        ]).pipe(
            switchMap(([crawledAt, orders]) => {
                const lnwIds = _uniq(
                    orders.map((order) => _toString(order._id)),
                );
                const sells: Observable<SellRow[]> = lnwIds.length
                    ? this.inChunks(lnwIds, (part) =>
                          this.unifiedQuery.query<SellRow>({
                              db: DB,
                              col: 'sales',
                              query: {
                                  type: { $in: ['sell', 'reversed'] },
                                  orderId: { $in: part },
                              },
                              options: {
                                  projection: {
                                      orderId: 1,
                                      pid: 1,
                                      type: 1,
                                      scrapedAt: 1,
                                  },
                              },
                          }),
                      )
                    : of([]);
                return sells.pipe(
                    switchMap((rows) =>
                        this.coveredPids(
                            stalePids(orderNos, orders, rows, crawledAt),
                            crawledAt,
                        ).pipe(
                            map((covered) => ({
                                states: saleStates(
                                    orderNos,
                                    orders,
                                    rows,
                                    crawledAt,
                                    covered,
                                ),
                                crawledAt,
                                failed: false,
                            })),
                        ),
                    ),
                );
            }),
            catchError((error: unknown) => {
                console.warn('lnwshop sale lookup failed —', error);
                return of(unavailable(orderNos));
            }),
        );
    }

    // Start of the newest sales crawl that finished 'ok'. Only the time
    // fields are read: a failed run's `reason` can carry a session URL.
    private latestSalesCrawl(): Observable<Moment | null> {
        return this.unifiedQuery
            .query<any>({
                db: DB,
                col: 'crawl_runs',
                query: { crawler: 'sales', status: 'ok' },
                options: {
                    sort: { startedAt: -1 },
                    limit: 1,
                    projection: { startedAt: 1 },
                },
            })
            .pipe(map((runs) => toMoment(runs[0]?.startedAt)));
    }

    // Which of `pids` the crawl that started at `crawledAt` re-scraped: any
    // of their sell rows scraped since. The query goes through $expr +
    // $toDate because unified-query passes no EJSON (a Date cannot be sent).
    private coveredPids(
        pids: string[],
        crawledAt: Moment | null,
    ): Observable<Set<string>> {
        if (pids.length === 0 || crawledAt === null) {
            return of(new Set<string>());
        }
        // pid is stored as a number; strings too, in case one is not.
        const asKeys = (part: string[]) => [
            ...part.map(Number).filter((pid) => Number.isFinite(pid)),
            ...part,
        ];
        return this.inChunks(pids, (part) =>
            this.unifiedQuery.aggregate<{ _id: unknown }>({
                db: DB,
                col: 'sales',
                pipeline: [
                    { $match: { type: 'sell', pid: { $in: asKeys(part) } } },
                    {
                        $match: {
                            $expr: {
                                $gte: [
                                    { $toDate: '$scrapedAt' },
                                    { $toDate: crawledAt.toISOString() },
                                ],
                            },
                        },
                    },
                    { $group: { _id: '$pid' } },
                ],
            }),
        ).pipe(map((rows) => new Set(rows.map((row) => _toString(row._id)))));
    }

    private inChunks<T>(
        keys: string[],
        fetch: (part: string[]) => Observable<T[]>,
    ): Observable<T[]> {
        return forkJoin(chunk(keys).map(fetch)).pipe(
            map((parts) => parts.flat()),
        );
    }
}
